import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { Link } from "@tanstack/react-router";
import { t } from "i18next";
import { EllipsisVerticalIcon, Trash } from "lucide-react";
import type { JSX } from "react";
import { StatusFile } from "@/common/enums/enums";
import { formatDuration, getFormattedDateToHour } from "@/lib/time";
import { cn, getVisitTitle } from "@/lib/utils";
import { AnimatedText } from "../AnimatedText";
import { CircleThinking } from "../CircleThinking";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../dropdown-menu";

interface VisitItemProps {
  number: number | null;
  date: string | null;
  duration: number | null;
  status: string | null;
  id: string;
  title?: string | null;
  isLoading?: boolean;
  onRequestDelete: () => void;
}

export function VisitItem({
  number,
  date,
  duration,
  status,
  id,
  title,
  isLoading = false,
  onRequestDelete,
}: VisitItemProps): JSX.Element {
  return (
    <div className="py-4 px-6 space-y-4">
      <div className="flex items-center justify-between ">
        <Link
          className="space-y-1 grow"
          to="/visits/$visitPublicId"
          params={{ visitPublicId: String(id) }}
        >
          <h3 className="text-primary text-lg font-bold">
            {getVisitTitle(title, number)}
          </h3>
          <p className="text-sm">{getFormattedDateToHour(date)}</p>
        </Link>
        <div className="flex items-center space-x-6">
          {status === StatusFile.PROCESSING.toString() ||
          status === StatusFile.GENERATING.toString() ||
          isLoading ? (
            <div className="w-5 h-5 border-2 border-t-primary border-primary/90 rounded-full animate-spin" />
          ) : (
            <span
              className={cn(
                "text-secondary font-bold",
                status == StatusFile.READY.toString() && "text-success",
                status == StatusFile.ERROR.toString() && "text-error",
              )}
            >
              {status == StatusFile.READY.toString() &&
                t("visit.statusFile.ready")}
              {status == StatusFile.ERROR.toString() &&
                t("visit.statusFile.error")}
            </span>
          )}
          <span>{formatDuration(duration)}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <EllipsisVerticalIcon className="cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuItem
                className="cursor-pointer flex items-center"
                onClick={onRequestDelete}
              >
                <Trash className="h-5 w-5" />
                <span>{t("visits.actions.deleteButton")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {status == StatusFile.GENERATING.toString() && (
        <div className="flex gap-2 items-center">
          <CircleThinking />
          <AnimatedText duration={3} variant="blink">
            {t("visit.components.visitItem.generatingNoteTemplate")}
          </AnimatedText>
        </div>
      )}
    </div>
  );
}
